import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { noticeError } from '@ratehub/base-ui';

/**
 * Error boundary component to track uncaught errors that get ignored because of
 * Next.js
 */
class ErrorBoundary extends Component { // Using Component because getDerivedStateFromError doesn't have an equal hook
    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { error: error, hasError: true };
    }

    state = {
        error: null,
        hasError: false,
        isNewRelicReady: false,
    };

    componentDidMount() {
        // Add an event listener to the window to catch unhandled promise rejections
        window.addEventListener('unhandledrejection', handlePromiseRejection);

        // Waits for New Relic 'agent' field to be populated which indicates New Relic ready state
        // before logging
        let newRelicCheckCount;
        const newRelicReadyInterval = window.setInterval(() => {
            newRelicCheckCount += 1;

            if (newRelicCheckCount >= MAX_NEW_RELIC_CHECK_COUNT) {
                window.clearInterval(newRelicReadyInterval);
                throw new Error('[ErrorBoundary] New Relic loading has timeout');
            } else if (window.newrelic?.info?.agent) {
                window.clearInterval(newRelicReadyInterval);
                this.setState({ isNewRelicReady: true });
            }
        }, 500);
    }

    componentDidUpdate(_prevProps, prevState) {
        if (
            this.state.isNewRelicReady !== prevState.isNewRelicReady && this.state.hasError
            || (this.state.error !== prevState.error && this.state.isNewRelicReady)
        ) {
            noticeError(this.state.error);
        }

    }

    componentWillUnmount() {
        window.removeEventListener('unhandledrejection', handlePromiseRejection);
    }

    render() {
        if (this.state.hasError) {
            return (
                <h3>
                    {'An Unexpected error has occurred'}
                </h3>

            );
        }
        return this.props.children;
    }
}

function handlePromiseRejection(event) {
    // Do not set error state here because promise errors should only be logged
    // because not all promise errors are app breaking.
    noticeError(event.reason, { 
        event,
        // We cannot get any valuable information out of the event
        //  unless the Promise.reject threw an Error. If we
        //  receive an Error we have access to the stack to see
        //  if this event actually occurred from our own code. 
        ...(event.reason instanceof Error 
            ? { stack: event.reason.stack }
            : {}
        ),
    });
}

const MAX_NEW_RELIC_CHECK_COUNT = 5;

ErrorBoundary.propTypes = {
    children: PropTypes.any.isRequired,
};

export default ErrorBoundary;
